/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/useTriageExpansion.ts
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-EXPANSION-001
 * PURPOSE: Resolve whether the context lanes are showing, from an explicit
 *   operator choice when one exists and a density-aware default when it
 *   doesn't.
 * ROLE IN ARCHITECTURE: Local UI-state hook under ReaderTriageBand.
 * MAIN EXPORTS: useTriageExpansion, READER_TRIAGE_EXPANDED_KEY.
 * NON-RESPONSIBILITIES: No decision building; the caller supplies the default.
 * NOTES FOR FUTURE AI: The stored value is a THREE-state contract — '1' open,
 *   '0' closed, absent means "operator has not chosen, use the default". Do not
 *   collapse it back to a boolean: an absent key is what lets the pointer-class
 *   default (open on desktop, closed on the mobile bottom sheet) apply at all.
 *
 *   SUPERSEDES D269-13 (operator 2026-07-25): a pending decision used to make
 *   every toggle temporary, so the lanes silently re-collapsed on the next
 *   email in a queue of unclassified mail. The operator's word for the context
 *   row is "the command centre" — an explicit open stays open, pending or not.
 */
import { useCallback, useState } from 'react'

/** Per-operator localStorage key for the expanded-context choice. */
export const READER_TRIAGE_EXPANDED_KEY = 'emailhub:reader-triage-expanded'

/** null = no explicit operator choice recorded yet. */
function readStoredPreference(): boolean | null {
  try {
    const raw = window.localStorage.getItem(READER_TRIAGE_EXPANDED_KEY)
    if (raw === '1') return true
    if (raw === '0') return false
    return null
  } catch {
    // Storage denied (private mode / embedded webview): fall back to default.
    return null
  }
}

function writeStoredPreference(next: boolean): void {
  try {
    window.localStorage.setItem(READER_TRIAGE_EXPANDED_KEY, next ? '1' : '0')
  } catch {
    // Best-effort persistence; the in-memory choice still applies.
  }
}

export interface TriageExpansionOptions {
  /**
   * Applied only until the operator chooses. Callers pass `false` for the
   * compact tier, where the panel is a bottom sheet over the body and opening
   * it unasked would cover the message.
   */
  defaultExpanded: boolean
}

export interface TriageExpansion {
  /** Whether the context lanes are showing right now. */
  expanded: boolean
  /** True once the operator has explicitly chosen (i.e. the default is spent). */
  chosen: boolean
  setExpanded: (next: boolean) => void
  toggle: () => void
}

/**
 * Resolve the expanded state from the stored choice and the caller's default.
 *
 * Inputs: the density-derived default. Output: expanded state + setters.
 * Side effects: reads/writes one localStorage key.
 * Failure mode: storage denied → the choice lives for the mount only.
 */
export function useTriageExpansion({
  defaultExpanded,
}: TriageExpansionOptions): TriageExpansion {
  const [preference, setPreference] = useState<boolean | null>(
    readStoredPreference,
  )
  const expanded = preference ?? defaultExpanded

  const setExpanded = useCallback((next: boolean) => {
    setPreference(next)
    writeStoredPreference(next)
  }, [])

  const toggle = useCallback(
    () => setExpanded(!expanded),
    [expanded, setExpanded],
  )

  return { expanded, chosen: preference !== null, setExpanded, toggle }
}
